import { createContext, useContext, useRef } from "react"
import { create, useStore } from "zustand"
import { chGeneralAttestationsFragment$key } from "@/modules/patients/detail/general-attestation/ch/services/__generated__/chGeneralAttestationsFragment.graphql"
import { deGeneralAttestationsFragment$key } from "@/modules/patients/detail/general-attestation/de/services/__generated__/deGeneralAttestationsFragment.graphql"

type State = {
  generalAttestationRef: deGeneralAttestationsFragment$key | chGeneralAttestationsFragment$key | null
  progressNoteId: string | null
  generalAttestationId: string | null
}

type Actions = {
  setProgressNoteId: (progressNoteId: string | null) => void
  setGeneralAttestationId: (
    generalAttestationId: string | null,
    ref: deGeneralAttestationsFragment$key | chGeneralAttestationsFragment$key | null,
  ) => void
  reset: () => void
}

const initialState: State = {
  progressNoteId: null,
  generalAttestationId: null,
  generalAttestationRef: null,
}

const createGeneralAttestationStore = () => {
  return create<State & Actions>((set, get) => ({
    ...initialState,
    setProgressNoteId: (progressNoteId) => {
      get().reset()
      set({ progressNoteId })
    },
    setGeneralAttestationId: (generalAttestationId, ref) => set({ generalAttestationId, generalAttestationRef: ref }),
    reset: () => {
      set(initialState)
    },
  }))
}

type GeneralAttestationStoreType = ReturnType<typeof createGeneralAttestationStore>

export const GeneralAttestationContext = createContext<GeneralAttestationStoreType | null>(null)

type GeneralAttestationProviderProps = React.PropsWithChildren

export const GeneralAttestationProvider = ({ children }: GeneralAttestationProviderProps) => {
  const storeRef = useRef<GeneralAttestationStoreType>(undefined)
  if (!storeRef.current) {
    storeRef.current = createGeneralAttestationStore()
  }
  return <GeneralAttestationContext.Provider value={storeRef.current}>{children}</GeneralAttestationContext.Provider>
}

export function useGeneralAttestationContext<T>(selector: (state: State & Actions) => T): T {
  const store = useContext(GeneralAttestationContext)
  if (!store) throw new Error("Missing GeneralAttestationProvider in the tree")
  return useStore(store, selector)
}
